import { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  TextField, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress,
  alpha,
  useTheme,
  Grow,
  Fade
} from '@mui/material';
import { login } from '../services/business';
import pierSunsetBg from '../images/pier_sunset.jpeg';
import smallLogo from '../images/short_logo.png';
import ecometricxLogo from '../images/ecometricx_logo.svg';
import titleLogo from '../images/title_gp.png';

interface LoginProps {
  onLogin: (businessId: string) => void;
}

const Login = ({ onLogin }: LoginProps) => {
  const [businessId, setBusinessId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const theme = useTheme();
  
  // Rotating titles state
  const titles = [
    "Your Business Sustainability Hub!",
    "Your Green Business Dashboard!",
    "Your Renewable Energy Center!",
    "Your Sustainable Business Tracker!",
    "Your Green Power Dashboard!",
    "Your Impact Metrics Center!",
    "Your Smart Energy Dashboard!",
    "Your Business Energy Hub!",
    "Your Low-Carbon Business Tracker!",
    "Your Green Usage Insights!"
  ];
  const [titleIndex, setTitleIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);

  // Animation sequence
  useEffect(() => {
    // Start animation sequence once component mounts
    setPageLoaded(true);
    
    // Show content after short delay to allow background to fade in
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 600);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Title rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlipping(true);
      
      setTimeout(() => {
        setTitleIndex((prev) => (prev + 1) % titles.length);
        setIsFlipping(false);
      }, 500);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [titles.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessId.trim()) {
      setError('Please enter your Business ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert to uppercase and trim whitespace
      const formattedBusinessId = businessId.trim().toUpperCase();
      console.log('Attempting login with formatted ID:', formattedBusinessId);
      
      // Use the new token-based login
      const userId = await login(formattedBusinessId);
      onLogin(userId);
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid Business ID. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Custom colors based on theme
  const primaryBlue = theme.palette.primary.dark; // Dark blue

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FFFFFF',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Background Image with Fade */}
      <Fade in={pageLoaded} timeout={1000}>
        <Box
          sx={{
            height: '100vh',
            width: '100%',
            position: 'absolute',
            backgroundImage: `url(${pierSunsetBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
        />
      </Fade>

      {/* Semi-transparent overlay */}
      <Fade in={pageLoaded} timeout={1200}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: alpha(primaryBlue, 0.6), // 60% opacity overlay
            zIndex: 1,
          }}
        />
      </Fade>

      <Grow in={showContent} timeout={800}>
        <Container 
          maxWidth="sm" 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            px: { xs: 2, sm: 3 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: { sm: '430px' },
            }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 3, sm: 4 },
                borderRadius: '20px',
                background: '#FFFFFF',
                width: '100%',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 3px 15px rgba(0, 0, 0, 0.06)',
                position: 'relative',
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                mb: 2,
              }}>
                {/* App Logo and Title */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 0.5,
                  }}
                >
                  {/* Main Logo and App Title */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 0.5,
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        component="img"
                        src={smallLogo}
                        alt="OCPA Logo"
                        sx={{
                          height: 45,
                          width: 'auto',
                          mr: 2,
                        }}
                      />
                      <Box
                        component="img"
                        src={titleLogo}
                        alt="GREEN P100NEER"
                        sx={{
                          height: 55,
                          width: 'auto',
                          filter: 'brightness(0) saturate(100%) invert(27%) sepia(65%) saturate(1500%) hue-rotate(118deg) brightness(104%) contrast(95%)',
                        }}
                      />
                    </Box>
                    
                    {/* Removed Orange County Power Authority subtitle */}
                  </Box>
                </Box>
                
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 0.5, 
                    fontWeight: 600,
                    color: primaryBlue,
                    textAlign: 'center',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Fade in={!isFlipping} timeout={500}>
                    <span>
                      {titles[titleIndex]}
                    </span>
                  </Fade>
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    mb: 3,
                  }}
                >
                  Please sign in with your Business ID to access your sustainability dashboard.
                </Typography>
              </Box>
              
              {/* Login form */}
              <Box 
                component="form" 
                onSubmit={handleSubmit} 
                sx={{ 
                  width: '100%',
                  mt: 2
                }}
              >
                <TextField
                  fullWidth
                  label="Business ID"
                  placeholder="Enter your Business ID"
                  variant="outlined"
                  value={businessId}
                  onChange={(e) => setBusinessId(e.target.value)}
                  error={Boolean(error)}
                  disabled={loading}
                  InputProps={{
                    sx: {
                      borderRadius: '10px',
                    },
                  }}
                  sx={{ mb: 2 }}
                />
                
                {error && (
                  <Alert 
                    severity="error"
                    sx={{ 
                      mt: 2,
                      borderRadius: 2,
                      fontSize: '0.85rem',
                    }}
                  >
                    {error}
                  </Alert>
                )}
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: error ? 2 : 2,
                    mb: 3,
                    py: 1.5,
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Log In'
                  )}
                </Button>
              </Box>
              
              {/* Footer with partner logo */}
              <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${alpha('#000000', 0.08)}` }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      mb: 1, 
                    }}
                  >
                    Powered by
                  </Typography>
                  <Box
                    component="img"
                    src={ecometricxLogo}
                    alt="ecometriCX logo"
                    sx={{
                      height: 20,
                      width: 'auto',
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Grow>
    </Box>
  );
};

export default Login; 